import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import LicenseModal from './LicenseModal';
import LicenseDetails from './LicenseDetails';
import { useNavigate } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import withPermission from '../../utils/withPermission';
import Swal from 'sweetalert2';
import { Offcanvas, Button, Form, Dropdown, OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';

const LicenseList = ({tenantId, onModalStateChange, onViewDetails, onShowModal, onShowExtendModal, onHideExtendModal, showExtendModal: propShowExtendModal}) => {
    const [licenses, setLicenses] = useState([]);
    const [licenseTypes, setLicenseTypes] = useState([]);
    const [expandColumns, setExpandColumns] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [localShowExtendModal, setLocalShowExtendModal] = useState(false);
    const showExtendModal = propShowExtendModal !== undefined ? propShowExtendModal : localShowExtendModal;
    const setShowExtendModal = (val, data = null) => {
        if (onShowExtendModal && val) {
             onShowExtendModal(data?.id || selectedLicenseId, data || selectedLicenseData);
        } else if (onHideExtendModal && !val) {
             onHideExtendModal();
        }
        setLocalShowExtendModal(val);
    };
    const [selectedLicenseId, setSelectedLicenseId] = useState(null);
    const [selectedLicenseData, setSelectedLicenseData] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    // const { id: tenantId } = useParams(); // Extract tenant_info_id from URL
    const didFetchRef = useRef(false);
    const [showMessage, setShowMessage] = useState(false);  // Controls visibility of the popup

    const [errors, setErrors] = useState({});
    const [extendValue, setExtendValue] = useState(1);
    const [extendType, setExtendType] = useState('days');
    const navigate = useNavigate();
    const [extendedMessage, setExtendedMessage] = useState('');
    const [labelClicked, setLabelClicked] = useState(false);
    const [expandedRow, setExpandedRow] = useState(null);
    const [showLicenseDetail, setShowLicenseDetail] = useState(false);
    const [viewLicenseId, setViewLicenseId] = useState(null);

    useEffect(() => {
        onModalStateChange?.(showModal || showExtendModal || showLicenseDetail);
    }, [showModal, showExtendModal, showLicenseDetail, onModalStateChange]);
    const COLS_VISIBLE = 3; // default visible license type columns
    const WithEditPermission = withPermission(({ children }) => children, ['license-edit']);
    const WithDeletePermission = withPermission(({ children }) => children, ['license-delete']);
    const WithCreatePermission = withPermission(({ children }) => children, ['license-create']);

    useEffect(() => {
        fetchLicenseTypes();
        fetchLicenses(statusFilter, tenantId, currentPage);
    }, [tenantId]);

    useEffect(() => {
        fetchLicenses(statusFilter, tenantId, currentPage);
    }, [statusFilter, currentPage]);

    const fetchLicenseTypes = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const response = await axios.get(`${backendUrl}/api/license-types`, {
                headers: { 'Authorization': `Bearer ${userInfo?.token}` , 'Tenant-Origin': origin},
                                skipLoader: true,

            });
            const dataObj = response.data.Data;
            setLicenseTypes(dataObj.data ? dataObj.data : (dataObj || []));
        } catch (error) {
            console.error('Error fetching license types:', error);
        }
    };
    const fetchLicenses = async (status = 'all', tid = null, page = 1) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const params = { status, page };
            if (tid) params.tenant_info_id = tid;

            const response = await axios.get(`${backendUrl}/api/licenses`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Tenant-Origin': origin },
                params,
                skipLoader: true,
            });

            const licenseData = response.data.Data.map(license => ({
                ...license,
                isExtended: license.extension_type !== null,
            }));

            setLicenses(licenseData);
            setPageCount(response.data.meta.total_pages);
        } catch (error) {
            console.error('Error fetching licenses:', error);
        }
    };

    const handleStatusChange = (event) => {
        const status = event.target.value;
        setStatusFilter(status);
        fetchLicenses(status, tenantId, currentPage);
    };

    const addLicense = async (newLicense) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            if (newLicense instanceof FormData) {
                newLicense.append('tenant_id', tenantId);
            }

            const response = await axios.post(`${backendUrl}/api/licenses`, newLicense, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    tenant_info_id: tenantId,
                },
            });

            const createdLicense = response.data?.data || response.data?.Data;
            if (createdLicense) {
                createdLicense.setup_name = createdLicense.instance.setup_name;
                setLicenses((prevLicenses) => [createdLicense, ...prevLicenses]);
            } else {
                console.error('Creation failed, no data returned.');
            }

            handleCloseModal();
        } catch (error) {
            if (error.response?.data?.errors) {
                return error.response.data.errors; // Return validation errors
            } else {
                console.error('Error adding license:', error);
            }
        }
    };

    const editLicense = async (licenseId, updatedData) => {
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
          const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
          const token = userInfo?.token;
          const { extension_type, extension_days } = (updatedData instanceof FormData) 
            ? { extension_type: updatedData.get('extension_type'), extension_days: updatedData.get('extension_days') }
            : updatedData;
      
          // Only extend if extension type and value are provided
          if (extension_type && extension_days) {
            // Validate extension data (optional)
            if (
              !['days', 'weeks', 'months', 'years'].includes(extension_type) ||
              isNaN(extension_days) || extension_days < 1
            ) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: 'Invalid extension data.',
                    timer: 3000,
                    timerProgressBar: false,
                    showConfirmButton: false,
                    didOpen: () => {
                      const container = Swal.getContainer();
                      container.style.top = '50px';
                    },
                  });             
                   return;
            }
          }
      
          let response;
          if (updatedData instanceof FormData) {
            // Add method spoofing in BOTH query string and body for maximum compatibility
            const updateUrl = `${backendUrl}/api/licenses/${licenseId}?_method=PUT`;
            if (updatedData instanceof FormData) {
                if (!updatedData.has('_method')) updatedData.append('_method', 'PUT');
            }

            response = await axios.post(updateUrl, updatedData, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Tenant-Origin': origin,
              },
            });
          } else {
            const updateUrl = `${backendUrl}/api/licenses/${licenseId}?_method=PUT`;
            response = await axios.post(updateUrl, updatedData, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Tenant-Origin': origin,
              },
              params: {
                tenant_info_id: tenantId,
              },
            });
          }
      
          const updatedLicense = response.data?.Data[0];
          if (updatedLicense) {
            updatedLicense.setup_name = updatedLicense.instance.setup_name;
      
            // Update isExtended flag based on extension data
            updatedLicense.isExtended = updatedLicense.extension_type !== null;
      
            setLicenses((prevLicenses) =>
              prevLicenses.map((license) =>
                license.id === updatedLicense.id ? updatedLicense : license
              )
            );
          } else {
            console.error('Update failed, no data returned.');
          }
      
          handleCloseModal();
        } catch (error) {
          console.error('Error updating license:', error);
        }
      };
      
    const deleteLicense = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
            didOpen: () => {
                const container = Swal.getContainer();
                if (container) container.style.zIndex = "999999";
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            await axios.delete(`${backendUrl}/api/licenses/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    tenant_info_id: tenantId, // Delete the license for this tenant
                },
            });

            setLicenses(licenses.filter(license => license.id !== id));
            Swal.fire({
                title: "Deleted!",
                text: "The license has been deleted.",
                icon: "success",
                didOpen: () => {
                    const container = Swal.getContainer();
                    if (container) container.style.zIndex = "999999";
                }
            });
        } catch (error) {
            console.error('Error deleting license:', error);
            Swal.fire({
                title: "Error!",
                text: "Failed to delete license. Please try again.",
                icon: "error",
                didOpen: () => {
                    const container = Swal.getContainer();
                    if (container) container.style.zIndex = "999999";
                }
            });
        }
    }
    });
    
    };



    const handleExtendClick = (license) => {
        setSelectedLicenseData(license);

        setExtendType('days');
        setExtendValue(1);
        setShowExtendModal(true, license);
    };


    const handleLabelClick = (license) => {
        // If the license is extended, generate a message
        if (license.isExtended) {
            const extensionValue = license.extension_days || license.extension_days;
            const extensionType = license.extension_type;
            const validityDate = new Date(license.validity || license.validity); // Use extended validity if available

            // Construct the message based on the extension type and value
            let extensionText = '';
            switch (extensionType) {
                case 'days':
                    extensionText = `${extensionValue} day${extensionValue > 1 ? 's' : ''}`;
                    break;
                case 'weeks':
                    extensionText = `${extensionValue} week${extensionValue > 1 ? 's' : ''}`;
                    break;
                case 'months':
                    extensionText = `${extensionValue} month${extensionValue > 1 ? 's' : ''}`;
                    break;
                case 'years':
                    extensionText = `${extensionValue} year${extensionValue > 1 ? 's' : ''}`;
                    break;
                default:
                    extensionText = `${extensionValue} ${extensionType}`;
                    break;
            }

            // Set the message and show it
            setExtendedMessage(`License extended for ${extensionText} until: ${validityDate.toLocaleDateString()}`);
            setShowMessage(true);
        } else {
            // Show the "Not Extended" message
            setExtendedMessage('This license is not extended yet.');
            setShowMessage(true);
        }
    };

    useEffect(() => {
        if (showMessage) {
            const timeout = setTimeout(() => {
                setShowMessage(false);  // Hide the message after 3 seconds
            }, 3000);

            // Cleanup timeout if component unmounts or message disappears
            return () => clearTimeout(timeout);
        }
    }, [showMessage]); // Trigger this effect when showMessage changes



    // const handleSubmitExtend = () => {
    //     if (selectedLicenseData) {
    //         const updatedData = {
    //             extension_type: extendType,
    //             extension_days: extendValue,
    //         };

    //         // Call editLicense with the selected license's ID and updated extension data
    //         editLicense(selectedLicenseData.id, updatedData);
    //         setShowExtendModal(false); // Close modal after submission
    //     }
    // };


    

    const handleShowModal = (licenseId = null) => {
        if (onShowModal) {
            const licenseToEdit = licenseId ? licenses.find((license) => license.id === licenseId) : null;
            onShowModal(licenseId, licenseToEdit);
        } else {
            if (licenseId) {
                const licenseToEdit = licenses.find((license) => license.id === licenseId);
                setSelectedLicenseData(licenseToEdit);
            } else {
                setSelectedLicenseData(null);
            }
            setSelectedLicenseId(licenseId);
            setShowModal(true);
        }
    };


    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedLicenseId(null);
        setSelectedLicenseData(null);
        setErrors({});

    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected + 1;
        setCurrentPage(newPage);
        fetchLicenses(statusFilter, tenantId, newPage); // Ensure tenant_info_id is passed with the page change
    };
    const handleViewDetails = (licenseId) => {
        if (onViewDetails) {
            onViewDetails(licenseId);
        } else {
            setViewLicenseId(licenseId);
            setShowLicenseDetail(true);
        }
    };
    const isActive = (license) => {
        const validityStr = license.extended_validity || license.validity || '';
        if (!validityStr) return true;
        const [y, m, d] = validityStr.split('-').map(Number);
        const expiryDate = new Date(y, m - 1, d);
        return expiryDate >= new Date().setHours(0,0,0,0);
    };

    const dynamicBreakdown = licenseTypes
        .filter(t => t.fields && Array.isArray(t.fields) && t.fields.length > 0)
        .map((type, idx) => ({
            name: type.name,
            fields: type.fields,
            recordType: type.type || 'license', // Fallback to license if undefined
            icon: ['mdi-security', 'mdi-headset', 'mdi-auto-fix', 'mdi-shield-check'][idx % 4],
            color: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'][idx % 5],
            bg: 'rgba(99, 102, 241, 0.08)'
        }));

    const allGroupedFields = dynamicBreakdown.flatMap(cat => cat.fields);
    const otherFields = licenseTypes.filter(t => !allGroupedFields.includes(t.name)).map(t => t.name);

    const getSummary = () => {
        let active    = 0;
        let expired   = 0;
        let extended  = 0;
        let totalsBreakdown = {};

        licenses.forEach(l => {
            const activeStatus = isActive(l);
            if (activeStatus) active++;
            else expired++;

            if (l.extension_type !== null) extended++;

            const customFields = l.custom_fields && typeof l.custom_fields === 'string'
                ? JSON.parse(l.custom_fields)
                : (l.custom_fields || {});

            licenseTypes.forEach(t => {
                const val = Number(l[t.name] || customFields[t.name] || 0);
                totalsBreakdown[t.name] = (totalsBreakdown[t.name] || 0) + val;
            });
        });

        return { 
            activeCount: active, 
            expiredCount: expired, 
            extendedCount: extended, 
            totalCount: licenses.length,
            totalsBreakdown 
        };
    };

    const summary = getSummary();
    const cardStyle = {
        borderRadius: '12px',
        transition: 'all 0.25s ease-in-out',
        border: '1px solid rgba(0,0,0,0.06)',
    };

    return (
        <>
            <div style={{ padding: '0 5px' }}>
                {/* Modern Filter Ribbon & Actions */}
                <div className="d-flex justify-content-between align-items-center mb-5 mt-2 bg-white p-3 rounded-4 shadow-sm border border-faint">
                    <div className="d-flex align-items-center gap-1">
                        <div className="bg-soft-primary p-2 rounded-3 me-2 d-none d-md-block">
                             <i className="mdi mdi-filter-variant text-primary font-size-18"></i>
                        </div>
                        
                        {/* Selection Dropdown - Primary Request */}
                        <Dropdown align="start">
                            <Dropdown.Toggle as="div" className="btn btn-light bg-soft-primary border-primary border-opacity-10 text-primary font-size-12 fw-bolder px-4 py-2 rounded-pill cursor-pointer text-uppercase d-flex align-items-center gap-2" style={{ letterSpacing: '0.8px' }}>
                                <i className="mdi mdi-apps"></i>
                                View Mode: {statusFilter}
                                <i className="mdi mdi-chevron-down opacity-50"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="shadow-lg border-0 rounded-3 mt-2">
                                {['all', 'active', 'extend', 'expired'].map(status => (
                                    <Dropdown.Item key={status} onClick={() => setStatusFilter(status)} className={`py-2 px-4 transition-all ${statusFilter === status ? 'bg-soft-primary text-primary fw-bold' : ''}`}>
                                        <i className={`mdi ${
                                            status === 'all' ? 'mdi-apps' : 
                                            status === 'active' ? 'mdi-check-circle-outline' : 
                                            status === 'extend' ? 'mdi-clock-fast' : 'mdi-alert-circle-outline'
                                        } me-2 font-size-16 align-middle`}></i>
                                        {status.charAt(0).toUpperCase() + status.slice(1)} {status === 'extend' ? 'Deployment' : 'Licenses'}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>

                        {/* Quick Action Ribbon (Hidden on small screens) */}
                        <div className="ms-3 d-none d-lg-flex gap-1 border-start ps-3 border-secondary border-opacity-10">
                            {['all', 'active', 'expired'].map(status => (
                                <Button
                                    key={status}
                                    variant="link"
                                    className={`px-3 py-1 rounded-pill border-0 font-size-11 fw-bolder transition-all text-uppercase ${statusFilter === status ? 'bg-primary text-white shadow-sm' : 'text-muted'}`}
                                    onClick={() => setStatusFilter(status)}
                                    style={{ textDecoration: 'none', letterSpacing: '0.4px' }}
                                >
                                    {status}
                                </Button>
                            ))}
                        </div>
                    </div>
                    
                    <div className="d-flex gap-2 align-items-center">
                        <Button 
                            variant="light" 
                            className={`rounded-pill px-3 shadow-sm border font-size-11 fw-bolder d-flex align-items-center gap-2 transition-all ${expandColumns ? 'bg-soft-primary text-primary border-primary' : 'text-muted'}`}
                            onClick={() => setExpandColumns(!expandColumns)}
                            style={{ height: '40px' }}
                        >
                            <i className={`mdi ${expandColumns ? 'mdi-view-column' : 'mdi-view-column-outline'} font-size-16`}></i>
                            <span>{expandColumns ? 'Summary View' : 'Full Detailed View'}</span>
                        </Button>
                        <WithCreatePermission>
                            <Button 
                                variant="primary" 
                                className="rounded-pill px-4 shadow-sm border-0 d-flex align-items-center gap-2 transition-all hover-transform" 
                                style={{ backgroundColor: '#6366f1', height: '40px' }}
                                onClick={() => handleShowModal()}
                            >
                                <i className="mdi mdi-plus-circle font-size-18"></i>
                                <span className="d-none d-sm-inline fw-bold font-size-12">New</span>
                            </Button>
                        </WithCreatePermission>
                    </div>
                </div>

            {/* Executive Dashboard Status KPIs - Refined for high-density summary */}
            {statusFilter !== 'active' && (
                <div className="row g-3 mb-5 mt-2">
                    {[
                        { label: 'Active Licenses', val: summary.activeCount, icon: 'mdi-check-circle-outline', color: '#10b981', bg: 'rgba(16, 185, 129, 0.08)' },
                        { label: 'Expired Records', val: summary.expiredCount, icon: 'mdi-clock-alert-outline', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.08)' },
                        { label: 'Extended Cases', val: summary.extendedCount, icon: 'mdi-rocket-launch-outline', color: '#6366f1', bg: 'rgba(99, 102, 241, 0.08)' },
                        { label: 'Total Inventory', val: summary.totalCount, icon: 'mdi-file-document-outline', color: '#74788d', bg: 'rgba(116, 120, 141, 0.08)' }
                    ].map((kpi, i) => (
                        <div className="col-xl-3 col-sm-6" key={i}>
                            <div className="card h-100 border-0 shadow-sm transition-all hover-shadow-md" style={{ borderRadius: '14px', border: '1px solid rgba(0,0,0,0.04)' }}>
                                <div className="card-body p-3 d-flex align-items-center">
                                    <div className="avatar-sm me-3 rounded-circle d-flex align-items-center justify-content-center" style={{ backgroundColor: kpi.bg, color: kpi.color, minWidth: '42px', height: '42px' }}>
                                        <i className={`mdi ${kpi.icon} font-size-18`}></i>
                                    </div>
                                    <div className="flex-grow-1 overflow-hidden">
                                        <p className="text-muted text-uppercase fw-bold font-size-10 mb-0 opacity-75 ls-1">{kpi.label}</p>
                                        <h4 className="mb-0 fw-bolder mt-1" style={{ color: '#1f2937' }}>{kpi.val}</h4>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Master Tenant Inventory Snapshot - Primary and Exclusive for Active Filter */}
            {statusFilter === 'active' && (
                <div className="card shadow-sm border-0 rounded-4 mb-5 overflow-hidden transition-all" style={{ ...cardStyle }}>
                    <div className="card-header bg-white py-4 px-4 border-bottom-0 d-flex justify-content-between align-items-center bg-soft-primary bg-opacity-10">
                        <div className="d-flex align-items-center">
                            <div className="bg-white p-2 rounded-3 me-3 shadow-sm border border-primary border-opacity-10">
                                <i className="mdi mdi-cube-scan text-primary font-size-22"></i>
                            </div>
                            <div>
                                <h5 className="mb-0 fw-bolder text-dark font-size-17">Active Inventory Dashboard</h5>
                                <p className="text-muted mb-0 font-size-11 opacity-75 pt-1">Cumulative license capacity and resource allocation snapshots</p>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                             <div className="d-flex gap-3">
                                 <div className="text-end d-none d-md-block bg-white p-2 rounded shadow-sm border border-primary border-opacity-10" style={{ minWidth: '110px' }}>
                                    <p className="mb-1 text-muted font-size-9 fw-bold text-uppercase ls-1 d-flex align-items-center justify-content-end gap-1"><i className="mdi mdi-certificate text-primary"></i> <span style={{ color: '#6366f1' }}>Total Licenses</span></p>
                                    <h6 className="mb-0 fw-bolder font-size-14" style={{ color: '#6366f1' }}>
                                        {licenseTypes.filter(t => t.type === 'license' || !t.type).reduce((sum, t) => sum + (summary.totalsBreakdown[t.name] || 0), 0)} <span className="font-size-9 text-muted fw-normal">UNITS</span>
                                    </h6>
                                 </div>
                                 <div className="text-end d-none d-md-block bg-white p-2 rounded shadow-sm border border-success border-opacity-10" style={{ minWidth: '110px' }}>
                                    <p className="mb-1 text-muted font-size-9 fw-bold text-uppercase ls-1 d-flex align-items-center justify-content-end gap-1"><i className="mdi mdi-puzzle text-success"></i> <span style={{ color: '#10b981' }}>Total Modules</span></p>
                                    <h6 className="mb-0 fw-bolder font-size-14" style={{ color: '#10b981' }}>
                                        {licenseTypes.filter(t => t.type === 'module').reduce((sum, t) => sum + (summary.totalsBreakdown[t.name] || 0), 0)} <span className="font-size-9 text-muted fw-normal">UNITS</span>
                                    </h6>
                                 </div>
                             </div>
                             <div className="divider-vertical mx-2 h-20px d-none d-md-block" style={{ borderRight: '1px solid rgba(0,0,0,0.1)' }}></div>
                             <WithCreatePermission>
                                <Button variant="primary" className="rounded-pill px-4 shadow-sm font-size-11 fw-bolder" onClick={() => handleShowModal()}>
                                    <i className="mdi mdi-plus-circle me-1"></i> NEW
                                </Button>
                             </WithCreatePermission>
                        </div>
                    </div>
                    <div className="card-body p-4 pt-1">
                        {summary.activeCount === 0 ? (
                            <div className="text-center py-5 border border-dashed rounded-4 my-3">
                                <i className="mdi mdi-alert-circle-outline font-size-48 text-muted opacity-25 d-block mb-3"></i>
                                <h6 className="text-muted fw-bolder font-size-13 text-uppercase ls-1">No Active Infrastructure Detected</h6>
                            </div>
                        ) : (
                            <div className="row g-4 pt-2">
                                {['license', 'module'].map((gType) => {
                                    const gCats = dynamicBreakdown.filter(c => c.recordType === gType);
                                    let hasItemsInGroup = false;

                                    const groupHtml = gCats.map((cat, idx) => {
                                        const activeItems = licenseTypes
                                            .filter(t => cat.fields.includes(t.name) && (summary.totalsBreakdown[t.name] || 0) > 0)
                                            .map(t => ({
                                                name: t.name.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
                                                val: summary.totalsBreakdown[t.name]
                                            }));

                                        if (activeItems.length === 0) return null;
                                        hasItemsInGroup = true;

                                        return (
                                            <div key={idx} className="col-12 mb-2">
                                                <div className="d-flex align-items-center mb-3">
                                                    <div className="rounded-pill p-1 me-2 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', background: `${cat.color}15` }}>
                                                        <i className={`mdi ${cat.icon} font-size-14`} style={{ color: cat.color }}></i>
                                                    </div>
                                                    <h6 className="mb-0 fw-bolder text-secondary font-size-11 text-uppercase ls-1 opacity-75">{cat.name} CATEGORY</h6>
                                                </div>
                                                <div className="row g-3">
                                                    {activeItems.map((item, iIndex) => (
                                                        <div key={iIndex} className="col-xl-4 col-lg-4 col-md-6 col-sm-12">
                                                            <div 
                                                                className="h-100 border-0 shadow-sm rounded-4 position-relative overflow-hidden" 
                                                                style={{ 
                                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
                                                                    cursor: 'pointer',
                                                                    backgroundColor: '#ffffff'
                                                                }} 
                                                                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.1)'; }} 
                                                                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 0.125rem 0.25rem rgba(0,0,0,0.075)'; }}
                                                            >
                                                                <div className="position-absolute top-0 start-0 w-100" style={{ height: '4px', backgroundColor: cat.color, opacity: '0.8', zIndex: 1 }}></div>
                                                                <div className="position-absolute top-0 start-0 w-100 h-100" style={{ backgroundColor: cat.color, opacity: '0.04', zIndex: 0 }}></div>
                                                                <div className="p-4 position-relative z-1 d-flex flex-column" style={{ minHeight: '110px' }}>
                                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                                        <div className="rounded-circle d-flex justify-content-center align-items-center shadow-sm bg-white" style={{ border: `1px solid ${cat.color}22`, width: '40px', height: '40px' }}>
                                                                            <i className={`mdi ${cat.icon} font-size-18`} style={{ color: cat.color }}></i>
                                                                        </div>
                                                                        <span className="badge rounded-pill px-3 py-2 font-size-16 fw-bolder shadow-sm" style={{ backgroundColor: cat.color, color: 'white' }}>
                                                                            {item.val}
                                                                        </span>
                                                                    </div>
                                                                    <div className="mt-auto">
                                                                        <h6 className="mb-0 font-size-13 fw-bolder text-dark text-truncate" title={item.name} style={{ letterSpacing: '0.3px' }}>{item.name}</h6>
                                                                        <small className="text-muted d-block mt-1 font-size-10 text-uppercase fw-bold opacity-75">{gType}</small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    });

                                    if (!hasItemsInGroup) return null;

                                    return (
                                        <div key={gType} className="col-12 mb-4">
                                            <div className="d-flex align-items-center mb-4 bg-light p-3 rounded-3 border-start border-4 shadow-sm" style={{ borderColor: gType === 'license' ? '#6366f1' : '#10b981' }}>
                                                <i className={`mdi ${gType === 'license' ? 'mdi-certificate' : 'mdi-puzzle'} font-size-22 me-2`} style={{ color: gType === 'license' ? '#6366f1' : '#10b981' }}></i>
                                                <h5 className="mb-0 fw-bolder text-dark text-uppercase ls-1" style={{ letterSpacing: '1px' }}>
                                                    {gType}S <span className="text-muted font-size-12 fw-normal ms-1">INVENTORY</span>
                                                </h5>
                                            </div>
                                            <div className="ms-3 border-start ps-4" style={{ borderLeftWidth: '2px !important', borderColor: 'rgba(0,0,0,0.05) !important' }}>
                                                {groupHtml}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Remaining Miscellaneous Assets - Listed plainly as requested */}
                                {(() => {
                                    const groupedFields = dynamicBreakdown.flatMap(cat => cat.fields);
                                    const remainingItems = licenseTypes
                                        .filter(t => !groupedFields.includes(t.name) && (summary.totalsBreakdown[t.name] || 0) > 0)
                                        .map(t => ({
                                            name: t.name.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
                                            val: summary.totalsBreakdown[t.name]
                                        }));

                                    if (remainingItems.length === 0) return null;

                                    return (
                                        <div className="col-12 mb-3 mt-4">
                                            <div className="d-flex align-items-center mb-3">
                                                <h6 className="mb-0 fw-bold text-muted font-size-10 text-uppercase ls-1 opacity-50">Miscellaneous Attributes</h6>
                                                <div className="ms-3 flex-grow-1 border-bottom border-light" style={{ height: '0', borderStyle: 'solid' }}></div>
                                            </div>
                                            <div className="row g-2">
                                                {remainingItems.map((item, iIndex) => (
                                                    <div key={iIndex} className="col-xl-4 col-lg-4 col-md-6 col-sm-12">
                                                        <div 
                                                            className="p-3 rounded-4 bg-light bg-opacity-10 border border-dotted h-100 d-flex justify-content-between align-items-center transition-all hover-bg-white shadow-xs-hover" 
                                                            style={{ borderColor: '#cbd5e1', cursor: 'pointer' }}
                                                        >
                                                            <div className="d-flex align-items-center me-2 text-truncate" style={{ maxWidth: '75%' }}>
                                                                <i className="mdi mdi-circle-small text-muted opacity-50 me-1"></i>
                                                                <h6 className="mb-0 font-size-12 fw-bolder text-secondary text-truncate" title={item.name} style={{ letterSpacing: '0.2px' }}>{item.name}</h6>
                                                            </div>
                                                            <div className="text-end">
                                                                <span className="badge rounded-pill px-2 py-1 font-size-11 fw-bolder text-dark bg-white shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                                                                    {item.val}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                    <div className="card-footer bg-light bg-opacity-20 border-top-0 py-3 px-4 text-center">
                         <span className="text-muted font-size-10 fw-bolder text-uppercase ls-1 opacity-50"><i className="mdi mdi-shield-check-outline me-1"></i> Real-time Active System State</span>
                    </div>
                </div>
            )}

            {/* Records History - Only for Non-Active Filters */}
            {statusFilter !== 'active' && (
                <div className="mt-2">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="d-flex align-items-center gap-3">
                            <h6 className="mb-0 fw-bolder text-dark font-size-14 ls-1">
                                <i className={`mdi mdi-history text-primary me-2 shadow-sm p-1 rounded bg-soft-primary`}></i>
                                {statusFilter.toUpperCase()} RECORDS HISTORY
                            </h6>
                            <span className="badge bg-light text-muted font-size-10 fw-bold border p-2 py-1 rounded-2">{licenses.length} RECORDS FOUND</span>
                        </div>
                    </div>

                    {/* High-Density Standard ERP Table for Historical Records */}
                    {/* High-Density Standard ERP Table */}
                    <div className="card border-0 shadow-sm rounded-4" style={{ ...cardStyle, overflow: 'visible' }}>
                        <div style={{ overflow: 'initial', minHeight: '400px', paddingBottom: '100px' }}>
                            <table className="table table-hover align-middle mb-0" style={{ overflow: 'visible' }}>
                                <thead className="bg-light bg-opacity-50">
                                    <tr className="border-bottom border-light">
                                        <th scope="col" style={{ width: '40px', position: 'sticky', left: 0, backgroundColor: '#f9fafb', zIndex: 11 }} className="ps-4">#</th>
                                        {!expandColumns ? (
                                            <>
                                                {dynamicBreakdown.map(cat => (
                                                    <th scope="col" key={cat.name} className="font-size-10 text-uppercase text-muted fw-bolder px-3" style={{ minWidth: '100px' }}>{cat.name}</th>
                                                ))}
                                                {otherFields.length > 0 && <th scope="col" className="font-size-10 text-uppercase text-muted fw-bolder px-3" style={{ minWidth: '80px' }}>{otherFields[0].split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}{otherFields.length > 1 ? '' : ''}</th>}
                                            </>
                                        ) : (
                                            licenseTypes.map(type => (
                                                <th scope="col" key={type.name} className="font-size-10 text-uppercase text-muted fw-bolder px-3">{type.name.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</th>
                                            ))
                                        )}
                                        <th scope='col' className="text-center font-size-10 text-uppercase text-muted fw-bolder px-3">Max Login</th>
                                        <th scope='col' className="text-center font-size-10 text-uppercase text-muted fw-bolder px-3">Max Create</th>
                                        <th scope="col" className="font-size-10 text-uppercase text-muted fw-bolder px-3">Start Date</th>
                                        <th scope="col" className="font-size-10 text-uppercase text-muted fw-bolder px-3">Validity</th>
                                        <th scope="col" className="font-size-10 text-uppercase text-muted fw-bolder px-3">Status</th>
                                        <th scope="col" className="font-size-10 text-uppercase text-muted fw-bolder px-3">Remarks</th>
                                        <th scope="col" className="text-end pe-4 font-size-10 text-uppercase text-muted fw-bolder px-3" style={{ position: 'sticky', right: 0, backgroundColor: '#f9fafb', zIndex: 11 }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                        {licenses.length === 0 ? (
                                            <tr><td colSpan="50" className="text-center py-5 text-muted font-size-13 italic">No matching records found in central archive.</td></tr>
                                        ) : licenses.map((license, index) => {
                                            const activeStatus = isActive(license);
                                            const customData = license.custom_fields && typeof license.custom_fields === 'string' ? JSON.parse(license.custom_fields) : (license.custom_fields || {});

                                            return (
                                                <tr 
                                                    key={license.id} 
                                                    className="border-bottom border-light hover-bg-light transition-all cursor-pointer"
                                                    onClick={() => handleViewDetails(license.id)}
                                                >
                                                    <td style={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 9 }} className="ps-4 font-size-12 fw-bold text-muted">{index + 1}</td>
                                                    {!expandColumns ? (
                                                        <>
                                                            {dynamicBreakdown.map(cat => {
                                                                const val = cat.fields.reduce((sum, f) => sum + (Number(license[f] || customData[f]) || 0), 0);
                                                                return <td key={cat.name} className={`px-3 font-size-12 ${val > 0 ? 'text-primary fw-bold' : 'text-muted opacity-25'}`}>{val || 0}</td>;
                                                            })}
                                                            {otherFields.length > 0 && (() => {
                                                                const val = Number(license[otherFields[0]] || customData[otherFields[0]]) || 0;
                                                                return <td key="others" className={`px-3 font-size-12 ${val > 0 ? 'text-primary fw-bold' : 'text-muted opacity-25'}`}>{val || 0}</td>;
                                                            })()}
                                                        </>
                                                    ) : (
                                                        licenseTypes.map((type) => {
                                                            const val = license[type.name] !== undefined ? license[type.name] : customData[type.name];
                                                            return <td key={type.name} className={`px-3 font-size-12 ${val > 0 ? 'text-primary fw-bold' : 'text-muted opacity-25'}`}>{val || 0}</td>;
                                                        })
                                                    )}
                                                    <td className="text-center font-size-12 fw-bolder text-dark px-3">{license.max_login || 0}</td>
                                                    <td className="text-center font-size-12 fw-bolder text-dark px-3">{license.max_create || 0}</td>
                                                    <td className="font-size-12 text-muted px-3">{license.start_date}</td>
                                                    <td className={`font-size-12 fw-bolder px-3 ${!activeStatus ? 'text-danger' : 'text-primary'}`}>{license.validity}</td>
                                                    <td className="px-3"><span className={`badge rounded-pill px-2 font-size-10 border ${!activeStatus ? 'bg-soft-danger text-danger border-danger border-opacity-10' : 'bg-soft-success text-success border-success border-opacity-10'}`}>{!activeStatus ? 'EXPIRED' : 'ACTIVE'}</span></td>
                                                    <td className="font-size-12 text-muted px-3" style={{ maxWidth: '150px' }}>
                                                        {license.remarks && license.remarks.length > 50 ? (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={<Tooltip id={`tooltip-remarks-${license.id}`}>{license.remarks}</Tooltip>}
                                                            >
                                                                <span className="text-truncate d-inline-block w-100" style={{ cursor: 'help' }}>
                                                                    {license.remarks.substring(0, 50)}...
                                                                </span>
                                                            </OverlayTrigger>
                                                        ) : (
                                                            <span className="text-truncate d-inline-block w-100">{license.remarks || '-'}</span>
                                                        )}
                                                    </td>
                                                <td style={{ position: 'sticky', right: 0, backgroundColor: 'white', zIndex: 9 }} className="text-end pe-4 px-3">
                                                    <div className="dropdown font-size-13" onClick={e => e.stopPropagation()}>
                                                        <i
                                                            className="mdi mdi-dots-vertical font-size-17 btn btn-sm btn-soft-secondary border-0 rounded"
                                                            data-bs-toggle="dropdown"
                                                            aria-expanded="false"
                                                            style={{ cursor: 'pointer' }}
                                                        ></i>
                                                        <ul className="dropdown-menu dropdown-menu-end shadow-lg border-0 rounded-3 font-size-14" style={{ minWidth: '220px', width: 'auto !important', zIndex: '999999 !important', overflow: 'visible !important' }} data-bs-boundary="viewport">
                                                            <li>
                                                                <button className="dropdown-item py-2 d-flex align-items-center" style={{ whiteSpace: 'nowrap !important' }} onClick={() => handleViewDetails(license.id)}>
                                                                    <i className="mdi mdi-eye-outline me-2 text-success font-size-16"></i> <span>View Details</span>
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <button className="dropdown-item py-2 d-flex align-items-center" style={{ whiteSpace: 'nowrap !important' }} onClick={() => handleExtendClick(license)}>
                                                                    <i className="mdi mdi-clock-fast me-2 text-primary font-size-16"></i> <span>Extend Date</span>
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <WithEditPermission>
                                                                    <button className="dropdown-item py-2 d-flex align-items-center" style={{ whiteSpace: 'nowrap !important' }} onClick={() => handleShowModal(license.id)}>
                                                                        <i className="mdi mdi-square-edit-outline me-2 text-info font-size-16"></i> <span>Edit</span>
                                                                    </button>
                                                                </WithEditPermission>
                                                            </li>
                                                            <li className="dropdown-divider"></li>
                                                            <li>
                                                                <WithDeletePermission>
                                                                    <button className="dropdown-item py-2 d-flex align-items-center text-danger" style={{ whiteSpace: 'nowrap !important' }} onClick={() => deleteLicense(license.id)}>
                                                                        <i className="mdi mdi-trash-can-outline me-2 font-size-16"></i> <span>Delete</span>
                                                                    </button>
                                                                </WithDeletePermission>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

          




            <br />

            <div className="d-flex justify-content-end pagination-sm mt-4">
                <ReactPaginate
                    previousLabel={'<'}
                    nextLabel={'>'}
                    breakLabel={'...'}
                    pageCount={pageCount}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={3}
                    onPageChange={handlePageChange}
                    containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                    pageClassName={'page-item'}
                    pageLinkClassName={'page-link'}
                    previousClassName={'page-item previous'}
                    previousLinkClassName={'page-link'}
                    nextClassName={'page-item next'}
                    nextLinkClassName={'page-link'}
                    breakClassName={'page-item'}
                    breakLinkClassName={'page-link'}
                    activeClassName={'active'}
                />
            </div>

            {!onShowModal && showModal && (
                <LicenseModal
                    showModal={showModal}
                    handleClose={handleCloseModal}
                    addLicense={addLicense}
                    editLicense={editLicense}
                    selectedLicenseId={selectedLicenseId}
                    selectedLicenseData={selectedLicenseData}
                    errors={errors}
                    setErrors={setErrors}
                    tenantId={tenantId}
                />
            )}

            {!onViewDetails && (
                <LicenseDetails
                    show={showLicenseDetail}
                    onHide={() => { setShowLicenseDetail(false); setTimeout(() => setViewLicenseId(null), 300); }}
                    licenseId={viewLicenseId}
                />
            )}
            </div>
        </>
    );
};

export default LicenseList;
